import React from 'react';
import { Field } from '../../components/ui';
import StepNav from './StepNav';

// Mobile "Setup Event" wizard — full-width, stacked fields, big buttons.
// Short fields share a row (date | time, venue | city, mobile | relation), the
// pax is a − N + stepper, mobile numbers open the number pad.
// Separate JSX from desktop; shared logic via the vm.
export default function SetupMobile({ vm }) {
  const {
    step, saving, canFunctions, canGuests,
    functions, fnOptions, activeFn, activeFnId, setActiveFnId, pickExistingFunction,
    fnForm, setFn, saveFunction,
    guestForm, setGuest, saveGuest, guestsAdded,
    memberForm, setMember, saveMember, membersAdded,
    FUNCTION_TYPE_OPTIONS, goStep,
    canImport, importGuestContact, importMemberContact,
  } = vm;

  // Name, mobile | relation and the pax stepper — same for guests and members.
  const personFields = (form, set, nameLabel) => {
    const n = Number(form.ExpectedCount || 0);
    const stepPax = (d) => set('ExpectedCount', String(Math.min(99, Math.max(1, n + d))));
    return (
      <>
        <Field label={nameLabel} value={form.GuestName} onChange={v => set('GuestName', v)} required />
        <div className="ev-m-pair">
          <label className="field">Mobile Number
            <input type="tel" inputMode="numeric" value={form.MobileNumber ?? ''} placeholder="10 digits"
              onChange={(e) => set('MobileNumber', e.target.value)} />
          </label>
          <Field label="Relation" value={form.Relation} onChange={v => set('Relation', v)} />
        </div>
        <div className="field">Expected Pax
          <div className="ev-stepper">
            <button type="button" onClick={() => stepPax(-1)} aria-label="One less">−</button>
            <input inputMode="numeric" value={form.ExpectedCount ?? ''} placeholder="1"
              onChange={(e) => set('ExpectedCount', e.target.value.replace(/\D/g, '').slice(0, 3))} />
            <button type="button" onClick={() => stepPax(1)} aria-label="One more">+</button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div>
      <div className="page-head"><h1>Setup Event</h1></div>
      <StepNav vm={vm} />

      {step === 1 && (
        <div className="card card-pad">
          {!canFunctions ? (
            <div className="empty">Only the Event Owner / Admin can add functions. Tap “2. Guests” to continue.</div>
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>Step 1 · Add a Function</h3>
              <Field label="Function Name" value={fnForm.FunctionName} onChange={v => setFn('FunctionName', v)} required />
              <Field label="Type" type="select" value={fnForm.FunctionType} options={FUNCTION_TYPE_OPTIONS} onChange={v => setFn('FunctionType', v)} />
              {fnForm.FunctionType === 'Other' && <Field label="New type name" value={fnForm.FunctionTypeOther} onChange={v => setFn('FunctionTypeOther', v)} />}
              <div className="ev-m-pair">
                <Field label="Date" type="date" value={fnForm.FunctionDate} onChange={v => setFn('FunctionDate', v)} />
                <Field label="Time" type="time" value={fnForm.FunctionTime} onChange={v => setFn('FunctionTime', v)} />
              </div>
              <div className="ev-m-pair">
                <Field label="Venue" value={fnForm.Venue} onChange={v => setFn('Venue', v)} />
                <Field label="City" value={fnForm.City} onChange={v => setFn('City', v)} />
              </div>
              <Field label="Venue Address" type="textarea" rows={2} value={fnForm.VenueAddress} onChange={v => setFn('VenueAddress', v)} />
              <Field label="GPS Location (lat,lng or Maps link)" type="textarea" rows={2} value={fnForm.GpsLocation} onChange={v => setFn('GpsLocation', v)} />
              <button className="btn primary" style={{ width: '100%', padding: 13, marginTop: 8 }} disabled={saving} onClick={saveFunction}>
                {saving ? 'Saving…' : 'Save & Add Guests →'}
              </button>

              {functions.length > 0 && (
                <div className="ev-setup-pick">
                  <div className="ev-hint">…or pick an existing function:</div>
                  <div className="ev-m-rows" style={{ marginBottom: 0 }}>
                    {functions.map(f => (
                      <button key={f.FunctionId} type="button" className="ev-m-row" onClick={() => pickExistingFunction(f.FunctionId)}>
                        <span className="ev-share-name">{f.FunctionName}</span>
                        <span className="ev-share-when">→</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="card card-pad">
          {!canGuests ? (
            <div className="empty">You don't have permission to add guests.</div>
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>Step 2 · Add Guests</h3>
              {activeFn && <div className="ev-hint">into “{activeFn.FunctionName}”</div>}
              <Field label="Function" type="select" value={activeFnId} options={fnOptions} onChange={setActiveFnId} required />
              {canImport && <button type="button" className="btn light" style={{ width: '100%', marginBottom: 8 }} onClick={importGuestContact}>📇 Pick from phone book</button>}
              {personFields(guestForm, setGuest, 'Guest Name')}
              <button className="btn primary" style={{ width: '100%', padding: 13, marginTop: 8 }} disabled={saving} onClick={() => saveGuest({ keepGoing: true })}>
                {saving ? 'Saving…' : '+ Add & Add Another'}
              </button>
              <button className="btn light" style={{ width: '100%', padding: 12, marginTop: 8 }} disabled={saving} onClick={() => saveGuest({ keepGoing: false })}>
                Add & Go to Members →
              </button>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
                <button className="btn light sm" onClick={() => goStep(3)}>Skip to Members →</button>
                <div style={{ flex: 1 }} />
                <span className="muted" style={{ fontSize: 13 }}>{guestsAdded} added</span>
              </div>
            </>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="card card-pad">
          {!canGuests ? (
            <div className="empty">You don't have permission to add members.</div>
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>Step 3 · Add Members</h3>
              <div className="ev-hint">
                A member is a person invited to the event — pick their function.
              </div>
              <Field label="Function" type="select" value={memberForm.FunctionId} options={fnOptions} onChange={v => setMember('FunctionId', v)} required />
              {canImport && <button type="button" className="btn light" style={{ width: '100%', marginBottom: 8 }} onClick={importMemberContact}>📇 Pick from phone book</button>}
              {personFields(memberForm, setMember, 'Member Name')}
              <button className="btn primary" style={{ width: '100%', padding: 13, marginTop: 8 }} disabled={saving} onClick={() => saveMember({ keepGoing: true })}>
                {saving ? 'Saving…' : '+ Add & Add Another'}
              </button>
              <button className="btn light" style={{ width: '100%', padding: 12, marginTop: 8 }} disabled={saving} onClick={() => saveMember({ keepGoing: false })}>
                Add & Finish
              </button>
              <div style={{ textAlign: 'right', marginTop: 10 }}>
                <span className="muted" style={{ fontSize: 13 }}>{membersAdded} added</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
